import { useMemo, useState, useCallback } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  createChannelIntegration,
  softDeleteChannelIntegration,
  updateChannelIntegration,
} from 'lane-shared/graphql/mutation';
import { getIntegrationDefinition } from 'application-types';
import { IntegrationProviderEnum } from 'constants-integrations';

export const notificationIntegrationProviders = [IntegrationProviderEnum.Cohesive];

export type ChannelIntegrationRecord = {
  _id: string;
  _created: string;
  _updated: string;
  deleted?: boolean | null;
  deletedAt?: string | null;
  settings?: Record<string, unknown> | null;
  integration: {
    _id: string;
    name: IntegrationProviderEnum;
    type: string;
  };
};

export type AvailableIntegrationRecord = {
  _id: string;
  name: IntegrationProviderEnum;
};

type NotificationIntegrationToggle = {
  provider: IntegrationProviderEnum;
  integrationId: string | null;
  channelIntegration: ChannelIntegrationRecord | null;
  isEnabled: boolean;
};

type UseNotificationIntegrationToggleOptions = {
  channelId: string | undefined;
  integrations: ChannelIntegrationRecord[];
  availableIntegrations: AvailableIntegrationRecord[];
  onSettled: () => Promise<void>;
};

export function useNotificationIntegrationToggle({
  channelId,
  integrations,
  availableIntegrations,
  onSettled,
}: UseNotificationIntegrationToggleOptions) {
  const { t } = useTranslation();
  const [isToggling, setIsToggling] = useState<Record<string, boolean>>({});

  const [createMutation] = useMutation(createChannelIntegration);
  const [softDeleteMutation] = useMutation(softDeleteChannelIntegration);
  const [updateMutation] = useMutation(updateChannelIntegration);

  const notificationIntegrations = useMemo<
    NotificationIntegrationToggle[]
  >(() => {
    const channelIntegrationsByName = new Map<
      IntegrationProviderEnum,
      ChannelIntegrationRecord
    >(
      integrations.map(integration => [
        integration.integration.name,
        integration,
      ])
    );
    const availableIntegrationsByName = new Map<
      IntegrationProviderEnum,
      AvailableIntegrationRecord
    >(
      availableIntegrations.map(integration => [
        integration.name,
        integration,
      ])
    );

    return notificationIntegrationProviders.map(provider => {
      const channelIntegration =
        channelIntegrationsByName.get(provider) || null;
      const availableIntegration =
        availableIntegrationsByName.get(provider) || null;
      const integrationId =
        channelIntegration?.integration?._id ||
        availableIntegration?._id ||
        null;
      const isEnabled =
        !!channelIntegration &&
        !channelIntegration.deleted &&
        !channelIntegration.deletedAt;

      return {
        provider,
        integrationId,
        channelIntegration,
        isEnabled,
      };
    });
  }, [availableIntegrations, integrations]);

  const toggleIntegration = useCallback(
    async (provider: IntegrationProviderEnum) => {
      const targetIntegration = notificationIntegrations.find(
        integration => integration.provider === provider
      );

      if (!targetIntegration || !channelId) {
        window.Toast.show(
          t('web.admin.channel.integrations.update.failure', {
            integrationName: provider,
          })
        );

        return;
      }

      setIsToggling(prevState => ({ ...prevState, [provider]: true }));

      try {
        if (targetIntegration.isEnabled) {
          if (!targetIntegration.channelIntegration?._id) {
            window.Toast.show(
              t('web.admin.channel.integrations.update.failure', {
                integrationName:
                  getIntegrationDefinition(provider).friendlyName,
              })
            );

            return;
          }

          await softDeleteMutation({
            variables: { id: targetIntegration.channelIntegration._id },
          });
        } else if (
          targetIntegration.channelIntegration?._id &&
          targetIntegration.channelIntegration.deleted
        ) {
          await updateMutation({
            variables: {
              channelIntegration: {
                _id: targetIntegration.channelIntegration._id,
                channel: { _id: channelId },
                integration: {
                  _id: targetIntegration.channelIntegration.integration._id,
                },
                settings:
                  targetIntegration.channelIntegration.settings || {},
              },
            },
          });
        } else {
          if (!targetIntegration.integrationId) {
            throw new Error(
              `${provider} integration is not available in integration settings.`
            );
          }

          await createMutation({
            variables: {
              channelIntegration: {
                channel: { _id: channelId },
                integration: { _id: targetIntegration.integrationId },
                settings: {},
              },
            },
          });
        }

        await onSettled();
      } catch {
        window.Toast.show(
          t('web.admin.channel.integrations.update.failure', {
            integrationName: getIntegrationDefinition(provider).friendlyName,
          })
        );
      } finally {
        setIsToggling(prevState => ({ ...prevState, [provider]: false }));
      }
    },
    [
      channelId,
      createMutation,
      notificationIntegrations,
      onSettled,
      softDeleteMutation,
      t,
      updateMutation,
    ]
  );

  return {
    notificationIntegrations,
    toggleIntegration,
    isToggling,
  };
}
